/*
 * dual-hero.js — Vanilla JS carousel for the homepage Dual Hero section.
 *
 * Behavior contract (Chunk D of PLAN_DUAL_HERO_AND_PODCAST):
 *   - Auto-rotate every data-autorotate-ms milliseconds (read off the root).
 *     Set to 0 (or missing) to disable auto-rotation.
 *   - prefers-reduced-motion: reduce → autoplay disabled, first slide stays
 *     active, dots/arrows still operable for manual navigation.
 *   - Pause on mouseenter / focusin; resume 1500ms after mouseleave / focusout.
 *   - Pause on document.visibilitychange when hidden; resume when visible.
 *   - Touch swipe: |dx| > 40 AND |dx| > |dy| → advance / retreat.
 *   - Keyboard: ArrowLeft / ArrowRight when focus is within carousel.
 *   - Dot click: data-dot-index jumps to that slide.
 *   - aria-live region: "Slide N of M: <headline>" on each slide change.
 *   - Auto-stop after 3 full rotations through all blocks. Resume only on
 *     explicit user nav (dot-click, arrow-key, swipe) — NOT mouseleave or
 *     focusout once stopped.
 *   - <2 blocks → no autoplay, dots row hidden by section markup.
 *
 * Vanilla ES5-ish — no imports, no jQuery, defer-safe.
 */

(function () {
  'use strict';

  // Idempotent install guard — survives accidental double-include.
  if (window.__ncDualHeroLoaded) return;
  window.__ncDualHeroLoaded = true;

  var RESUME_DELAY_MS = 1500;
  var SWIPE_THRESHOLD_PX = 40;
  var MAX_ROTATIONS = 3;

  function init() {
    var roots = document.querySelectorAll('[data-dual-hero]');
    if (!roots.length) return;
    for (var i = 0; i < roots.length; i++) {
      try {
        new DualHero(roots[i]);
      } catch (e) {
        // Never throw from a theme asset — log and move on.
        if (window.console && console.warn) {
          console.warn('[dual-hero] init failed for instance', i, e);
        }
      }
    }
  }

  /**
   * Per-instance carousel controller. One DualHero is constructed per
   * `[data-dual-hero]` root on the page (theme editor may render multiple).
   */
  function DualHero(root) {
    this.root = root;
    this.slides = root.querySelectorAll('.dual-hero__slide');
    this.dots = root.querySelectorAll('.dual-hero__dot');
    this.liveRegion = root.querySelector('.dual-hero__live-region');
    this.total = this.slides.length;

    if (this.total < 1) return;

    this.currentIndex = 0;
    this.autorotateMs = parseInt(root.getAttribute('data-autorotate-ms'), 10) || 0;
    this.timerId = null;
    this.resumeTimerId = null;
    this.rotationCount = 0;
    this.autoStopped = false;
    this.touchStartX = 0;
    this.touchStartY = 0;

    // Reduced-motion preference — read once at construction.
    var rmQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reducedMotion = !!(rmQuery && rmQuery.matches);

    // Disable autoplay entirely when there's only one slide or fewer.
    this.canAutoplay =
      this.total >= 2 &&
      this.autorotateMs > 0 &&
      !this.reducedMotion;

    this.bindEvents();
    this.start();
  }

  DualHero.prototype.bindEvents = function () {
    var self = this;

    // Pause on hover.
    this.root.addEventListener('mouseenter', function () { self.pause(); });
    this.root.addEventListener('mouseleave', function () { self.scheduleResume(); });

    // Pause on focus-within.
    this.root.addEventListener('focusin', function () { self.pause(); });
    this.root.addEventListener('focusout', function (e) {
      // Only resume when focus actually leaves the carousel root.
      if (!self.root.contains(e.relatedTarget)) {
        self.scheduleResume();
      }
    });

    // Pause when the page becomes hidden; resume when visible again.
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') {
        self.pause();
      } else {
        self.scheduleResume();
      }
    });

    // Dot click → jump.
    for (var i = 0; i < this.dots.length; i++) {
      var dot = this.dots[i];
      dot.addEventListener('click', (function (idx) {
        return function () {
          self.goTo(idx, /* userInitiated */ true);
        };
      })(i));
    }

    // Keyboard arrows — bound to the carousel root so they only fire when
    // focus is within the carousel.
    this.root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        self.previous(/* userInitiated */ true);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        self.next(/* userInitiated */ true);
      }
    });

    // Touch swipe.
    this.root.addEventListener('touchstart', function (e) {
      if (!e.touches || !e.touches.length) return;
      self.touchStartX = e.touches[0].clientX;
      self.touchStartY = e.touches[0].clientY;
    }, { passive: true });

    this.root.addEventListener('touchend', function (e) {
      if (!e.changedTouches || !e.changedTouches.length) return;
      var dx = e.changedTouches[0].clientX - self.touchStartX;
      var dy = e.changedTouches[0].clientY - self.touchStartY;
      // Bail out when the gesture is dominantly vertical (page scroll).
      if (Math.abs(dy) > Math.abs(dx)) return;
      if (Math.abs(dx) <= SWIPE_THRESHOLD_PX) return;
      if (dx < 0) {
        self.next(/* userInitiated */ true);
      } else {
        self.previous(/* userInitiated */ true);
      }
    }, { passive: true });
  };

  DualHero.prototype.start = function () {
    // Make sure the visible slide matches our internal index, and announce.
    this.render();
    if (this.canAutoplay && !this.autoStopped) {
      this.play();
    }
  };

  DualHero.prototype.play = function () {
    if (!this.canAutoplay || this.autoStopped) return;
    this.clearTimer();
    var self = this;
    this.timerId = setInterval(function () {
      self.advance(/* fromAutoplay */ true);
    }, this.autorotateMs);
  };

  DualHero.prototype.pause = function () {
    this.clearTimer();
    this.clearResumeTimer();
  };

  DualHero.prototype.clearTimer = function () {
    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  };

  DualHero.prototype.clearResumeTimer = function () {
    if (this.resumeTimerId !== null) {
      clearTimeout(this.resumeTimerId);
      this.resumeTimerId = null;
    }
  };

  DualHero.prototype.scheduleResume = function () {
    if (!this.canAutoplay || this.autoStopped) return;
    this.clearResumeTimer();
    var self = this;
    this.resumeTimerId = setTimeout(function () {
      self.resumeTimerId = null;
      self.play();
    }, RESUME_DELAY_MS);
  };

  /**
   * Auto-advance step. When the slide returns to index 0, increment the
   * rotation counter; after MAX_ROTATIONS full rotations, stop autoplay
   * entirely. Resume requires explicit user nav (next/previous/goTo with
   * userInitiated=true).
   */
  DualHero.prototype.advance = function (fromAutoplay) {
    var nextIndex = (this.currentIndex + 1) % this.total;
    this.currentIndex = nextIndex;

    if (fromAutoplay && nextIndex === 0) {
      this.rotationCount += 1;
      // Stop autoplay after >= 3 full rotations (vestibular protection).
      if (this.rotationCount >= 3 && this.rotationCount >= MAX_ROTATIONS) {
        this.autoStopped = true;
        this.clearTimer();
      }
    }

    this.render();
  };

  DualHero.prototype.next = function (userInitiated) {
    this.currentIndex = (this.currentIndex + 1) % this.total;
    if (userInitiated) {
      this.handleUserNav();
    }
    this.render();
  };

  DualHero.prototype.previous = function (userInitiated) {
    this.currentIndex = (this.currentIndex - 1 + this.total) % this.total;
    if (userInitiated) {
      this.handleUserNav();
    }
    this.render();
  };

  DualHero.prototype.goTo = function (index, userInitiated) {
    if (index < 0 || index >= this.total) return;
    this.currentIndex = index;
    if (userInitiated) {
      this.handleUserNav();
    }
    this.render();
  };

  /**
   * Explicit user nav (dot click, arrow key, swipe) re-enables autoplay if
   * it had previously auto-stopped after 3 rotations. Per spec §6: vestibular
   * users get out of the auto-rotation loop, but a deliberate interaction
   * indicates engagement and resumes the cycle.
   */
  DualHero.prototype.handleUserNav = function () {
    if (this.autoStopped) {
      this.autoStopped = false;
      this.rotationCount = 0;
      if (this.canAutoplay) this.play();
    } else {
      // Don't compete with the user — pause and schedule a resume.
      this.pause();
      this.scheduleResume();
    }
  };

  /**
   * Update DOM to reflect this.currentIndex:
   *   - data-active="true" on the active slide (CSS uses this to show it)
   *   - aria-current="true" on the matching dot
   *   - tabindex on the active slide so keyboard can target it
   *   - aria-live region text for screen readers
   */
  DualHero.prototype.render = function () {
    var i;
    for (i = 0; i < this.slides.length; i++) {
      var slide = this.slides[i];
      var isActive = i === this.currentIndex;
      if (isActive) {
        slide.setAttribute('data-active', 'true');
        slide.setAttribute('tabindex', '-1');
      } else {
        slide.removeAttribute('data-active');
        slide.setAttribute('tabindex', '-1');
      }
    }

    for (i = 0; i < this.dots.length; i++) {
      var dot = this.dots[i];
      if (i === this.currentIndex) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    }

    this.announce();
  };

  DualHero.prototype.announce = function () {
    if (!this.liveRegion) return;
    var slide = this.slides[this.currentIndex];
    if (!slide) return;
    var headlineEl = slide.querySelector('.dual-hero__slide-headline');
    var headline = headlineEl ? (headlineEl.textContent || '').trim() : '';
    var msg = 'Slide ' + (this.currentIndex + 1) + ' of ' + this.total;
    if (headline) msg += ': ' + headline;
    this.liveRegion.textContent = msg;
  };

  // DOM-ready entry point — defer-safe.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
